import React from 'react';
import ReactPrismic from 'prismic-reactjs';
import RouterLink from 'next/link';
import { useRouter } from 'next/router';
import { ArticleJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { DiscussionEmbed } from 'disqus-react';
import { parse, startOfDay } from 'date-fns';

import { DISQUS_SHORTNAME } from '@/config';
import { BlogPostDocument } from '@/domains/blog';
import { urls } from '@/constants';

import Seo from '@/components/Seo';
import Container from '@/components/Container';
import Content from '@/components/Content';
import { withRichText, Element, CMSHeading1, CMSHeading2, CMSHeading3 } from '@/components/CMS';
import Typography from '@/components/Typography';
import Spacer from '@/components/Spacer';
import CodeSnippetSlice from '@/components/CMS/CodeSnippetSlice';
import { Tags, Tag, TagContent } from '@/components/Tag';
import FormattedDate from '@/components/FormattedDate';

import logo from '@/assets/favicon.svg';

import style from './BlogPostPage.sass';

export interface Props {
  document: BlogPostDocument;
}

export interface Query {
  [x: string]: any;
  slug: string;
}

const RichText = withRichText({
  elements: {
    [Element.Heading1]: (props) => (
      <Spacer xs={6} type="margin">
        <CMSHeading1 {...props} underline />
      </Spacer>
    ),
    [Element.Heading2]: (props) => (
      <Spacer xs={4} type="margin">
        <CMSHeading2 {...props} />
      </Spacer>
    ),
    [Element.Heading3]: (props) => (
      <Spacer xs={4} type="margin">
        <CMSHeading3 {...props} />
      </Spacer>
    )
  }
});

const BlogPostPage: React.FC<Props> = ({ document }) => {
  const router = useRouter();

  const as = urls.blogPost(router.query.slug as string).as;
  const title = ReactPrismic.RichText.asText(document.data.title);

  return (
    <React.Fragment>
      <Seo
        title={title}
        description={ReactPrismic.RichText.asText(document.data.meta_description)}
        images={[document.data.meta_image.url]}
        type="article"
      />

      <ArticleJsonLd
        url={urls.getAbsoluteUrl(as)}
        title={title}
        images={document.data.image ? [document.data.image.url] : []}
        datePublished={startOfDay(parse(document.data.published_at, 'yyyy-MM-dd', new Date())).toISOString()}
        authorName="Botond Veress"
        description={ReactPrismic.RichText.asText(document.data.description)}
        publisherName="Botond Veress"
        publisherLogo={urls.getAbsoluteUrl(logo)}
      />

      <BreadcrumbJsonLd itemListElements={[{ name: 'Blog', item: urls.getAbsoluteUrl(urls.blog), position: 1 }]} />

      <article>
        <Container>
          <Content>
            <Spacer xs={8} />

            <RichText render={document.data.title} />

            <Spacer xs={8}>
              <div className={style.information}>
                <Tags>
                  <Tag>
                    <TagContent is={Typography}>
                      <FormattedDate date={document.data.published_at} />
                    </TagContent>
                  </Tag>

                  {document.tags.map((tag) => (
                    <Tag key={tag}>
                      <RouterLink {...urls.blogCategory(tag)} passHref>
                        <TagContent is={(props: any) => <Typography {...props} is="a" />}>{tag}</TagContent>
                      </RouterLink>
                    </Tag>
                  ))}
                </Tags>
              </div>
            </Spacer>

            {document.data.body.map((slice, index) => {
              switch (slice.slice_type) {
                case 'code_snippet':
                  return (
                    <Spacer key={index} xs={4} md={4} type="margin">
                      <CodeSnippetSlice
                        code={ReactPrismic.RichText.asText(slice.primary.snippet)}
                        file={slice.primary.file}
                        language={slice.primary.template}
                        highlight={slice.primary.highlight}
                      />
                    </Spacer>
                  );

                case 'paragraph':
                  return (
                    <Spacer key={index} xs={4} md={4} type="margin">
                      <div>
                        {slice.items.map((item, index) => (
                          <RichText key={index} render={item.paragraph} />
                        ))}
                      </div>
                    </Spacer>
                  );
              }

              return null;
            })}

            <Spacer xs={6} md={8} type="margin" />

            {DISQUS_SHORTNAME && (
              <React.Fragment>
                <DiscussionEmbed
                  shortname={DISQUS_SHORTNAME}
                  config={{
                    url: urls.getAbsoluteUrl(as),
                    identifier: as,
                    title,
                    language: 'en'
                  }}
                />

                <Spacer xs={6} md={8} type="margin" />
              </React.Fragment>
            )}
          </Content>
        </Container>
      </article>
    </React.Fragment>
  );
};

export default BlogPostPage;
